import {
  CheckCircle,
  MinusCircle,
  PageRight,
  RefreshDouble,
  WarningCircle,
  XmarkCircle
} from 'iconoir-react';

import { RunDeployment } from '../hooks/useDeploymentsData';

import i18n from '~/common/helpers/i18n';
import { Deployment } from '~/entities/Deployment';

export const REPLICA_WARNING_THRESHOLD = 10;

export const exceedsReplicaWarningThreshold = (
  replicas?: number | string
): boolean => {
  const value = Number(replicas);
  return !Number.isNaN(value) && value > REPLICA_WARNING_THRESHOLD;
};

export const DEPLOYMENT_SIZE_CONSUMERS = [10, 20, 40];

export const getDeploymentSizeLabel = (consumers?: number): string => {
  if (consumers === 10) return i18n.t('scenes.runtime.labels.size_small');
  if (consumers === 20) return i18n.t('scenes.runtime.labels.size_medium');
  if (consumers === 40) return i18n.t('scenes.runtime.labels.size_large');
  return '';
};

export const getDeploymentEngineRelease = (
  deploymentStatus?: {
    pipelineEngineVersion?: string;
    pipelineEngineRelease?: string;
  } | null
) =>
  deploymentStatus?.pipelineEngineRelease ||
  deploymentStatus?.pipelineEngineVersion ||
  '';

const [SMALL_CONSUMERS, MEDIUM_CONSUMERS, LARGE_CONSUMERS] =
  DEPLOYMENT_SIZE_CONSUMERS;

export const getDeploymentSizeAbbreviation = (consumers?: number): string => {
  if (consumers === SMALL_CONSUMERS)
    return i18n.t('scenes.runtime.labels.size_small_short');
  if (consumers === MEDIUM_CONSUMERS)
    return i18n.t('scenes.runtime.labels.size_medium_short');
  if (consumers === LARGE_CONSUMERS)
    return i18n.t('scenes.runtime.labels.size_large_short');
  return '';
};

export const shortenEngineVersion = (engineVersion: string): string =>
  engineVersion.replace(/^([^.]*\.[^.]*\.[^.]*).*/, '$1');

export type StatusKey =
  | 'deleting'
  | 'starting'
  | 'degraded'
  | 'error'
  | 'deployed'
  | 'service_error'
  | 'service_active'
  | 'redeploy'
  | 'running'
  | 'redeployed'
  | 'service_active_but_degraded';

export type StatusInfo = {
  text: string;
  icon: React.ComponentType<{ className?: string }>;
};

export const transformDeploymentStatus = (
  deployment: Deployment
): Partial<RunDeployment> => {
  const isStatusError = deployment.status === 'SERVICE_ERROR';

  const statusFormated =
    deployment.status.substring(0, 1) +
    deployment.status.substring(1).toLowerCase();

  return {
    ...deployment,
    status: {
      text: isStatusError ? 'Error' : statusFormated
    }
  } as Partial<RunDeployment>;
};

export const getStatusParamValue = (
  status: string | string[] | null
): string[] => {
  if (!status || status === 'null') return [];

  if (Array.isArray(status)) return status;

  if (typeof status === 'string' && status.includes(',')) {
    return status.split(',').filter(s => s !== '');
  }

  if (status?.length > 0) return [status];

  return [];
};

export const getMessageStatus = ({
  deployStatus
}: {
  deployStatus?: StatusKey;
}): StatusInfo => {
  const statusMap = {
    deleting: {
      text: i18n.t('label.deleting'),
      icon: MinusCircle
    },
    starting: {
      text: i18n.t('label.starting'),
      icon: PageRight
    },
    degraded: {
      text: i18n.t('label.starting'),
      icon: PageRight
    },
    error: {
      text: i18n.t('label.error'),
      icon: XmarkCircle
    },
    deployed: {
      text: i18n.t('label.deployed'),
      icon: CheckCircle
    },
    service_error: {
      text: i18n.t('label.error'),
      icon: XmarkCircle
    },
    service_active: {
      text: i18n.t('label.deployed'),
      icon: CheckCircle
    },
    redeploy: {
      text: i18n.t('scenes.runtime.labels.redeploy'),
      icon: RefreshDouble
    },
    running: {
      text: i18n.t('label.deployed'),
      icon: CheckCircle
    },
    redeployed: {
      text: i18n.t('label.deployed'),
      icon: CheckCircle
    },
    service_active_but_degraded: {
      text: i18n.t('common.labels.degraded'),
      icon: WarningCircle
    }
  };

  const statusSelected = deployStatus;
  if (statusSelected && statusSelected in statusMap) {
    return statusMap[statusSelected as StatusKey];
  }
  return statusMap.deployed;
};
